import { useI18n } from "@/i18n/I18nProvider";
import { Reveal } from "@/hooks/useReveal";
import { Lock } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Premium client logo placeholders.
 * Mono-only, shaped marks with sector + NDA tag — designed to read as
 * "real, redacted clients" rather than generic logo bar.
 */
export function ClientsSection({ index = "06", className, hideEyebrow }: { index?: string; className?: string; hideEyebrow?: boolean }) {
  const { t } = useI18n();
  const data = t.clientsBlock;
  const items = data.placeholders as { name: string; sector: string }[];

  return (
    <section className="border-t hairline bg-foreground/[0.015]">
      <div className={cn("mx-auto max-w-7xl px-6 lg:px-10 py-24 lg:py-32", className)}>
        <div className="grid lg:grid-cols-12 gap-10 mb-14">
          <Reveal className="lg:col-span-6">
            {!hideEyebrow && (
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-4">
                — {index} / {data.eyebrow}
              </p>
            )}
            <h2 className="font-display text-4xl lg:text-6xl font-semibold tracking-tighter">
              {data.title}
            </h2>
          </Reveal>
          <Reveal delay={120} className="lg:col-span-5 lg:col-start-8 lg:pt-4">
            <p className="text-lg text-muted-foreground leading-relaxed text-pretty">
              {data.subtitle}
            </p>
          </Reveal>
        </div>

        <div className="grid gap-px bg-border border hairline rounded-2xl overflow-hidden sm:grid-cols-2 lg:grid-cols-4">
          {items.map((c, i) => (
            <Reveal key={c.name} delay={i * 50} className="bg-background">
              <div className="group relative h-full p-6 lg:p-8 card-lift">
                <p className="font-display text-base font-semibold tracking-tight">{c.name}</p>
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground mt-1">
                  {c.sector}
                </p>
                <span className="mt-3 inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-muted-foreground/70">
                  <Lock className="h-3 w-3" strokeWidth={1.6} />
                  NDA
                </span>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={200} className="mt-10">
          <p className="text-sm text-muted-foreground leading-relaxed max-w-3xl">
            {data.footer}
          </p>
        </Reveal>
      </div>
    </section>
  );
}
